import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { AuthListener, authService } from 'src/api/services/auth-service';
import { BusinessLocation } from 'src/api/types/business-types';
import { AuthStatus, Role, SyntraxisUser } from 'src/api/types/types';
import { auth } from 'src/api/client';
import { getDateString } from 'src/utils/app/string';

export enum AppStateID {
  // eslint-disable-next-line no-unused-vars
  Location,
  // eslint-disable-next-line no-unused-vars
  Loading,
  // eslint-disable-next-line no-unused-vars
  Language,
  // eslint-disable-next-line no-unused-vars
  Date,
}

type UpdateProp = BusinessLocation | string | boolean;

const SELECTED_LOCATION_KEY = 'selectedLocationKey';

interface AppContextType {
  isAdmin: boolean;
  strength: number;
  isLoading: boolean;
  hasAuthError: boolean;
  date: string;
  language: string;
  isUserLoggedIn: boolean;
  user?: SyntraxisUser;
  location?: BusinessLocation;
  // eslint-disable-next-line no-unused-vars
  updateState: (updateId: AppStateID, value: UpdateProp) => void;
}

const initialContext: AppContextType = {
  isAdmin: false,
  hasAuthError: false,
  isLoading: true,
  date: '',
  strength: 0,
  language: 'en',
  isUserLoggedIn: false,
  updateState: () => {},
};

const Context = createContext<AppContextType>(initialContext);

export const useAppAuth = (): AppContextType => {
  const context = useContext(Context);

  if (!context) {
    throw new Error('useAppAuth must be used within AuthProvider');
  }

  return context;
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [hasAuthError, setHasAuthError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [date, setDate] = useState<string>(getDateString());
  const [user, setUser] = useState<SyntraxisUser | undefined>(undefined);
  const [location, setLocation] = useState<BusinessLocation | undefined>();
  const [language, setLanguage] = useState<string>('en');
  const [authInitialized, setAuthInitialized] = useState<boolean>(false);
  const [authResolving, setAuthResolving] = useState<boolean>(false);

  const strength = 10;
  const isAdmin = user?.role === Role.admin;

  const updateState = (updateId: AppStateID, value: UpdateProp) => {
    switch (updateId) {
      case AppStateID.Location:
        setLocation(value as BusinessLocation);
        break;
      case AppStateID.Language:
        setLanguage(value as string);
        break;
      case AppStateID.Date:
        setDate(value as string);
        break;
      case AppStateID.Loading:
        setIsLoading(value as boolean);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const unsubscribeAuthState = onAuthStateChanged(auth, currentUser => {
      if (!currentUser) {
        setUser(undefined);
        setLocation(undefined);
        localStorage.removeItem(SELECTED_LOCATION_KEY);
        setAuthResolving(false);
        setIsLoading(false);
        setAuthInitialized(true);
        return;
      }
      // Firebase has a user; wait for authService to hydrate app user
      setAuthResolving(true);
      setAuthInitialized(true);
    });

    const listener: AuthListener = {
      onUserUpdated: (userData: SyntraxisUser) => {
        setUser(userData);
        setAuthResolving(false);
        setAuthInitialized(true);
        setIsLoading(false);
      },
      onError: (error: Error) => {
        // Log auth errors; eslint disabled locally for controlled console usage
        // eslint-disable-next-line no-console
        console.error(error);
        setIsLoading(false);
        setAuthResolving(false);
        setAuthInitialized(true);
        setUser(undefined);
        if (
          (error as unknown as { cause?: AuthStatus }).cause ===
          AuthStatus.notFound
        ) {
          // Stay signed-in but mark waitlist gate; attempt to register in waitlist
          setHasAuthError(true);
        } else {
          authService.signOut();
        }
      },
    };

    const unsubscribe = authService.addListener(listener);

    return () => {
      unsubscribe();
      unsubscribeAuthState();
    };
  }, []);

  useEffect(() => {
    if (user && !location && user.locations.length > 0) {
      const storedKey = localStorage.getItem(SELECTED_LOCATION_KEY);
      const fromStorage = user.locations.find(
        loc => loc.key === storedKey && loc.disabled !== true,
      );
      if (fromStorage) {
        setLocation(fromStorage);
      } else {
        const firstEnabled = user.locations.find(loc => loc.disabled !== true);
        if (firstEnabled) {
          setLocation(firstEnabled);
        }
      }
    }

    if (authInitialized && !authResolving) {
      setIsLoading(false);
    }
  }, [user, location, user?.locations, authInitialized, authResolving]);

  // Persist selection when it changes
  useEffect(() => {
    if (location?.key) {
      localStorage.setItem(SELECTED_LOCATION_KEY, location.key);
    }
  }, [location?.key]);

  const contextValue: AppContextType = useMemo(
    () => ({
      date,
      user,
      isAdmin,
      language,
      location,
      strength,
      isLoading,
      updateState,
      hasAuthError,
      isUserLoggedIn: !!user,
    }),
    [date, user, location, strength, isLoading, language],
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};
